import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";

const router = createRouter({
  history: createWebHistory((import.meta as any).env.BASE_URL),
  routes: [
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/auth/Login.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/auth/forgot-password",
      name: "forgot-password",
      component: () => import("@/views/auth/ForgotPassword.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/",
      component: () => import("@/components/layout/AppShell.vue"),
      meta: { requiresAuth: true },
      children: [
        {
          path: "",
          name: "home",
          component: () => import("@/views/dashboard/HomeView.vue"),
        },
        {
          path: "sales/dashboard",
          name: "sales-dashboard",
          component: () => import("@/views/sales/SalesDashboardView.vue"),
        },

        {
          path: "sales/sellers",
          name: "Vendedores",
          component: () => import("@/views/sales/SellersView.vue"),
        },
        {
          path: "sales/sellers/:id",
          name: "SellerDetail",
          component: () => import("@/views/sales/SellerDetailView.vue"),
        },
        {
          path: "sales/commission-rules",
          name: "sales-commission-rules",
          component: () => import("@/views/sales/CommissionRulesView.vue"),
        },
        {
          path: "sales/orders",
          name: "sales-orders",
          component: () => import("@/views/sales/OrdersView.vue"),
        },
        {
          path: "sales/subscriptions/new",
          name: "sales-subscriptions-new",
          component: () => import("@/views/sales/SubscriptionCreateView.vue"),
          meta: { title: 'ISPManager - Nueva Suscripción', requiresAuth: true },
        },
        {
          path: "sales/subscriptions",
          name: "sales-subscriptions",
          component: () => import("@/views/sales/SubscriptionsView.vue"),
        },
        {
          path: "sales/subscriptions/:id",
          name: "sales-subscription-detail",
          component: () => import("@/views/sales/SubscriptionDetailView.vue"),
        },
        {
          path: "sales/goals",
          name: "sales-goals",
          component: () => import("@/views/sales/SalesGoalsView.vue"),
        },
        {
          path: "sales/commissions",
          name: "sales-commissions",
          component: () => import("@/views/sales/CommissionsView.vue"),
        },
        {
          path: "crm/dashboard",
          name: "crm-dashboard",
          component: () => import("@/views/crm/DashboardView.vue"),
        },
        {
          path: "crm/reports",
          name: "crm-reports",
          component: () => import("@/views/crm/ReportsView.vue"),
        },
        {
          path: "crm/leads",
          name: "crm-leads",
          component: () => import("@/views/crm/LeadsView.vue"),
        },
        {
          path: "crm/leads/new",
          name: "crm-leads-create",
          component: () => import("@/views/crm/LeadFormView.vue"),
        },
        {
          path: "crm/leads/pending",
          name: "crm-leads-pending",
          component: () => import("@/views/crm/PendingLeadsView.vue"),
        },
        {
          path: "crm/leads/kanban",
          name: "crm-leads-kanban",
          component: () => import("@/views/crm/LeadsKanbanView.vue"),
        },
        {
          path: "crm/leads/:id",
          name: "crm-leads-detail",
          component: () => import("@/views/crm/LeadDetailView.vue"),
        },
        {
          path: "crm/leads/:id/edit",
          name: "crm-leads-edit",
          component: () => import("@/views/crm/LeadFormView.vue"),
        },
        {
          path: "crm/opportunities",
          name: "crm-opportunities",
          component: () => import("@/views/crm/OpportunitiesView.vue"),
        },
        {
          path: "crm/opportunities/kanban",
          name: "crm-opportunities-kanban",
          component: () => import("@/views/crm/OpportunitiesKanbanView.vue"),
        },
        {
          path: "crm/opportunities/new",
          name: "crm-opportunities-create",
          component: () => import("@/views/crm/OpportunityFormView.vue"),
        },
        {
          path: "crm/opportunities/:id",
          name: "crm-opportunities-detail",
          component: () => import("@/views/crm/OpportunityDetailView.vue"),
        },
        {
          path: "crm/opportunities/:id/edit",
          name: "crm-opportunities-edit",
          component: () => import("@/views/crm/OpportunityFormView.vue"),
        },
        {
          path: "crm/opportunities/:id/onboard",
          name: "crm-opportunities-onboard",
          component: () => import("@/views/crm/OnboardingWizardView.vue"),
        },
        {
          path: "crm/contacts",
          name: "crm-contacts",
          component: () => import("@/views/crm/ContactsView.vue"),
        },
        {
          path: "crm/contacts/new",
          name: "crm-contacts-create",
          component: () => import("@/views/crm/ContactFormView.vue"),
        },
        {
          path: "crm/contacts/:id",
          name: "crm-contacts-detail",
          component: () => import("@/views/crm/ContactDetailView.vue"),
        },
        {
          path: "crm/contacts/:id/edit",
          name: "crm-contacts-edit",
          component: () => import("@/views/crm/ContactFormView.vue"),
        },
        {
          path: "customers",
          name: "customers",
          component: () => import("@/views/customers/CustomersView.vue"),
        },
        {
          path: "customers/new",
          name: "customer-create",
          component: () => import("@/views/customers/CustomerFormView.vue"),
        },
        {
          path: "customers/:id",
          name: "customer-detail",
          component: () => import("@/views/customers/CustomerDetailView.vue"),
        },
        {
          path: "customers/:customerId/invoices",
          name: "InvoiceList",
          props: true,
          component: () => import("@/views/billing/InvoiceListView.vue"),
        },
        {
          path: "billing/invoices",
          name: "billing-invoices",
          component: () => import("@/views/billing/InvoiceListView.vue"),
        },
        {
          path: "billing/invoices/:id",
          name: "billing-invoice-detail",
          component: () => import("@/views/billing/InvoiceDetailView.vue"),
        },
        {
          path: "billing/credit-notes",
          name: "billing-credit-notes",
          component: () => import("@/views/billing/CreditNotesView.vue"),
        },
        {
          path: "customers/:customerId/pending-charges",
          name: "billing-pending-charges",
          props: true,
          component: () => import("@/views/billing/PendingChargesView.vue"),
        },
        {
          path: "billing/runs",
          name: "billing-runs",
          component: () => import("@/views/billing/BillingRunsView.vue"),
        },
        {
          path: "billing/runs/:id",
          name: "billing-run-detail",
          props: true,
          component: () => import("@/views/billing/BillingRunDetailView.vue"),
        },
        {
          path: "reports/financial",
          name: "reports-financial",
          component: () => import("@/views/reports/FinancialReportsView.vue"),
        },
        {
          path: "reports/network",
          name: "reports-network",
          component: () => import("@/views/reports/NetworkReportsView.vue"),
        },
        {
          path: "reports/sales",
          name: "reports-sales",
          component: () => import("@/views/reports/SalesReportsView.vue"),
        },
        {
          path: "customers/:customerId/invoices/:invoiceId",
          name: "InvoiceDetail",
          component: () => import("@/views/billing/InvoiceDetailView.vue"),
        },
        {
          path: "customers/:id/edit",
          name: "customer-edit",
          redirect: (to) => {
            return {
              name: "customer-detail",
              params: { id: to.params.id },
              query: { edit: "true" },
            };
          },
        },
        {
          path: "services",
          name: "services",
          component: () => import("@/views/services/ServicesView.vue"),
        },
        {
          path: "services/:id",
          name: "service-detail",
          component: () => import("@/views/services/ServiceDetailView.vue"),
        },
        {
          path: "services/:id/edit",
          name: "service-edit",
          component: () => import("@/views/services/ServiceEditView.vue"),
        },
        {
          path: "crm/proposals",
          name: "crm-proposals",
          component: () => import("@/views/crm/ProposalsView.vue"),
        },
        {
          path: "crm/proposals/new",
          name: "crm-proposals-create",
          component: () => import("@/views/crm/ProposalFormView.vue"),
        },
        {
          path: "crm/proposals/:id",
          name: "crm-proposals-detail",
          component: () => import("@/views/crm/ProposalDetailView.vue"),
        },
        {
          path: "crm/proposals/:id/edit",
          name: "crm-proposals-edit",
          component: () => import("@/views/crm/ProposalFormView.vue"),
        },

        {
          path: "crm/quotes",
          name: "crm-quotes",
          component: () => import("@/views/crm/QuotesView.vue"),
        },
        {
          path: "crm/quotes/new",
          name: "crm-quotes-create",
          component: () => import("@/views/crm/QuoteFormView.vue"),
        },
        {
          path: "crm/quotes/:id",
          name: "crm-quotes-detail",
          component: () => import("@/views/crm/QuoteDetailView.vue"),
        },
        {
          path: "crm/quotes/:id/edit",
          name: "crm-quotes-edit",
          component: () => import("@/views/crm/QuoteFormView.vue"),
        },
        {
          path: "crm/reminders/new",
          name: "crm-reminders-create",
          component: () => import("@/views/crm/ReminderFormView.vue"),
        },
        {
          path: "crm/reminders",
          name: "crm-reminders",
          component: () => import("@/views/crm/RemindersView.vue"),
        },
        {
          path: "crm/settings/lost-reasons",
          name: "crm-settings-lost-reasons",
          component: () => import("@/views/crm/LostReasonsView.vue"),
        },
        {
          path: "crm/settings/lead-origins",
          name: "crm-settings-lead-origins",
          component: () => import("@/views/crm/LeadOriginsView.vue"),
        },
        {
          path: "crm/settings/lead-sources",
          name: "crm-settings-lead-sources",
          component: () => import("@/views/crm/LeadSourcesView.vue"),
        },
        {
          path: "crm/settings/lead-statuses",
          name: "crm-settings-lead-statuses",
          component: () => import("@/views/crm/LeadStatusesView.vue"),
        },
        {
          path: "crm/settings/tags",
          name: "crm-settings-tags",
          component: () => import("@/views/crm/TagsView.vue"),
        },
        {
          path: "settings/templates",
          name: "settings-templates",
          component: () => import("@/views/crm/templates/ReportTemplatesView.vue"),
        },
        {
          path: "settings/templates/new",
          name: "settings-templates-create",
          component: () => import("@/views/crm/templates/ReportTemplateFormView.vue"),
        },
        {
          path: "settings/templates/:id/edit",
          name: "settings-templates-edit",
          component: () => import("@/views/crm/templates/ReportTemplateFormView.vue"),
        },
        {
          path: "settings/roles",
          name: "settings-roles",
          component: () => import("@/views/settings/RolesView.vue"),
          meta: {
            title: "Roles y Permisos",
          },
        },
        {
          path: "settings/audit",
          name: "settings-audit",
          component: () => import("@/views/settings/AuditLogView.vue"),
          meta: {
            title: "Registro de Auditoría",
          },
        },
        {
          path: "settings/users",
          name: "settings-users",
          component: () => import("@/views/settings/UsersView.vue"),
          meta: {
            title: "Usuarios",
          },
        },
        {
          path: "settings/users/:id",
          name: "settings-user-detail",
          component: () => import("@/views/settings/UserDetailView.vue"),
          meta: {
            title: "Detalle de Usuario",
          },
        },
        {
          path: "erp/inventory",
          name: "erp-inventory",
          component: () => import("@/views/erp/InventoryView.vue"),
        },
        {
          path: "erp/purchasing",
          name: "erp-purchasing",
          component: () => import("@/views/erp/PurchasingView.vue"),
        },
        {
          path: "erp/purchasing/receive",
          name: "erp-purchasing-receive",
          component: () =>
            import("@/views/erp/purchasing/GoodsReceiptView.vue"),
        },
        {
          path: "erp/purchasing/suppliers",
          name: "erp-purchasing-suppliers",
          component: () => import("@/views/erp/purchasing/SuppliersView.vue"),
        },
        {
          path: "erp/purchasing/suppliers/new",
          name: "erp-purchasing-supplier-create",
          component: () =>
            import("@/views/erp/purchasing/SupplierFormView.vue"),
        },
        {
          path: "erp/purchasing/suppliers/:id",
          name: "erp-purchasing-supplier-detail",
          component: () =>
            import("@/views/erp/purchasing/SupplierFormView.vue"),
        },
        {
          path: "erp/equipment",
          name: "erp-equipment",
          component: () => import("@/views/erp/EquipmentView.vue"),
        },
        {
          path: "erp/work-orders",
          name: "erp-work-orders",
          component: () => import("@/views/erp/WorkOrdersView.vue"),
        },
        {
          path: "erp/work-orders/new",
          name: "erp-work-orders-new",
          component: () =>
            import("@/views/erp/work_orders/WorkOrderFormView.vue"),
        },
        {
          path: "erp/work-orders/:id/edit",
          name: "erp-work-orders-edit",
          component: () =>
            import("@/views/erp/work_orders/WorkOrderFormView.vue"),
        },
        {
          path: "erp/work-orders/:id",
          name: "erp-work-order-detail",
          component: () =>
            import("@/views/erp/work_orders/WorkOrderDetailView.vue"),
        },
        // SDD 10: Infrastructure routes removed (nodes/zones now managed by ms-0015-networking)
        {
          path: "erp/expenses",
          name: "erp-expenses",
          component: () => import("@/views/erp/expenses/ExpensesView.vue"),
        },
        {
          path: "erp/expenses/new",
          name: "erp-expenses-new",
          component: () => import("@/views/erp/expenses/ExpenseFormView.vue"),
        },
        {
          path: "erp/accounting/accounts",
          name: "erp-accounting-accounts",
          component: () => import("@/views/erp/accounting/AccountsView.vue"),
        },
        {
          path: "erp/accounting/settings/cash-registers",
          name: "erp-accounting-cash-registers",
          component: () => import("@/views/erp/accounting/CashRegistersView.vue"),
        },
        {
          path: "erp/accounting/reports/cash-sessions",
          name: "erp-accounting-cash-sessions-report",
          component: () => import("@/views/erp/accounting/CashSessionsReportView.vue"),
          meta: { permission: 'reports.financial.read' }
        },
        {
          path: "my-cash-registers",
          name: "my-cash-registers",
          component: () => import("@/views/erp/accounting/CashRegistersView.vue"),
          meta: { myOnly: true },
        },
        {
          path: "erp/accounting/settings/cash-registers/:id",
          name: "erp-accounting-cash-register-detail",
          component: () => import("@/views/erp/accounting/CashRegisterDetailView.vue"),
        },
        {
          path: "erp/payments",
          name: "erp-payments",
          component: () => import("@/views/erp/payments/PaymentsView.vue"),
        },
        {
          path: "billing/payments",
          name: "billing-payments",
          alias: ["payments", "gateway"],
          component: () => import("@/views/billing/payments/PaymentsView.vue"),
        },
        {
          path: "billing/payments/:id",
          name: "billing-payment-detail",
          component: () => import("@/views/billing/payments/PaymentDetailView.vue"),
        },
        {
          path: "erp/checks",
          name: "erp-checks",
          component: () => import("@/views/erp/payments/ChecksView.vue"),
        },
        {
          path: "erp/accounting/journals",
          name: "erp-accounting-journals",
          component: () => import("@/views/erp/accounting/JournalsView.vue"),
        },
        {
          path: "erp/hr/employees",
          name: "erp-hr-employees",
          component: () => import("@/views/erp/hr/EmployeesView.vue"),
        },
        {
          path: "erp/hr/employees/new",
          name: "erp-hr-employees-create",
          component: () => import("@/views/erp/hr/EmployeeFormView.vue"),
        },
        {
          path: "erp/hr/employees/:id",
          name: "erp-hr-employees-detail",
          component: () => import("@/views/erp/hr/EmployeeDetailView.vue"),
        },
        {
          path: "erp/hr/employees/:id/edit",
          name: "erp-hr-employees-edit",
          component: () => import("@/views/erp/hr/EmployeeFormView.vue"),
        },
        {
          path: "erp/hr/departments",
          name: "erp-hr-departments",
          component: () => import("@/views/erp/hr/DepartmentsView.vue"),
        },
        {
          path: "erp/offices",
          name: "erp-offices",
          component: () => import("@/views/erp/OfficesView.vue"),
        },
        {
          path: "erp/offices/new",
          name: "erp-office-create",
          component: () => import("@/views/erp/OfficeFormView.vue"),
        },
        {
          path: "erp/offices/:id",
          name: "erp-office-detail",
          component: () => import("@/views/erp/OfficeDetailView.vue"),
        },
        {
          path: "erp/offices/:id/edit",
          name: "erp-office-edit",
          component: () => import("@/views/erp/OfficeFormView.vue"),
        },
        {
          path: "erp/products",
          alias: "erp/catalog",
          name: "erp-products",
          component: () => import("@/views/erp/ProductsView.vue"),
        },
        {
          path: "erp/catalog/ai-models",
          alias: "erp/ai-models",
          name: "erp-ai-models",
          component: () => import("@/views/erp/AiModelsListView.vue"),
          meta: {
            title: "Modelos de IA & Inferencia",
            requiresAuth: true,
          },
        },
        {
          path: "erp/products/new",
          name: "erp-product-create",
          component: () => import("@/views/erp/ProductFormView.vue"),
        },
        {
          path: "erp/products/:id",
          name: "erp-product-detail",
          component: () => import("@/views/erp/ProductDetailView.vue"),
        },
        {
          path: "erp/products/:id/edit",
          name: "erp-product-edit",
          component: () => import("@/views/erp/ProductFormView.vue"),
        },
        {
          path: "erp/settings/units",
          name: "erp-units",
          component: () => import("@/views/erp/UnitsView.vue"),
        },
        {
          path: "erp/settings/units/new",
          name: "erp-unit-new",
          component: () => import("@/views/erp/UnitFormView.vue"),
        },
        {
          path: "erp/settings/units/:id",
          name: "erp-unit-edit",
          component: () => import("@/views/erp/UnitFormView.vue"),
        },
        {
          path: "erp/warehouse",
          name: "erp-warehouse",
          component: () => import("@/views/erp/WarehouseView.vue"),
        },
        {
          path: "erp/warehouse/branch",
          name: "erp-warehouse-branch",
          component: () =>
            import("@/views/erp/warehouse/BranchWarehouseView.vue"),
          meta: { permissions: ["erp.warehouse.read"] },
        },
        {
          path: "erp/warehouse/counts",
          name: "erp-warehouse-counts",
          component: () =>
            import("@/views/erp/warehouse/PhysicalCountsView.vue"),
        },
        {
          path: "erp/warehouse/counts/new",
          name: "erp-warehouse-counts-create",
          component: () =>
            import("@/views/erp/warehouse/PhysicalCountDetailView.vue"),
        },
        {
          path: "erp/warehouse/counts/:id",
          name: "erp-warehouse-counts-detail",
          component: () =>
            import("@/views/erp/warehouse/PhysicalCountDetailView.vue"),
        },
        {
          path: "erp/warehouse/transfers",
          name: "erp-warehouse-transfers",
          component: () =>
            import("@/views/erp/warehouse/TransfersView.vue"),
        },
        {
          path: "erp/warehouse/transfers/new",
          name: "erp-warehouse-transfers-create",
          component: () =>
            import("@/views/erp/warehouse/TransferFormView.vue"),
        },
        {
          path: "erp/warehouse/transfers/:id",
          name: "erp-warehouse-transfers-detail",
          component: () =>
            import("@/views/erp/warehouse/TransferDetailView.vue"),
        },
        {
          path: "erp/warehouse/new",
          name: "erp-warehouse-create",
          component: () => import("@/views/erp/WarehouseFormView.vue"),
          meta: { permissions: ["erp.warehouse.create"] },
        },
        {
          path: "erp/warehouse/:id",
          name: "erp-warehouse-detail",
          component: () => import("@/views/erp/WarehouseDetailView.vue"),
        },
        {
          path: "erp/warehouse/:id/edit",
          name: "erp-warehouse-edit",
          component: () => import("@/views/erp/WarehouseFormView.vue"),
          meta: { permissions: ["erp.warehouse.update"] },
        },
        {
          path: "erp/infrastructure",
          name: "erp-infrastructure",
          component: () => import("@/views/erp/InfrastructureView.vue"),
        },
        {
          path: "erp/fixed-assets",
          name: "erp-fixed-assets",
          component: () =>
            import("@/views/erp/fixed_assets/FixedAssetsView.vue"),
        },
        {
          path: "erp/manufacturing/orders",
          alias: "erp/manufacturing",
          name: "erp-manufacturing-orders",
          component: () =>
            import("@/views/erp/manufacturing/ManufacturingOrdersView.vue"),
        },
        {
          path: "erp/maintenance/plans",
          name: "erp-maintenance-plans",
          component: () =>
            import("@/views/erp/maintenance/MaintenancePlansView.vue"),
        },
        {
          path: "helpdesk/tickets",
          name: "helpdesk-tickets",
          component: () => import("@/views/helpdesk/TicketsView.vue"),
        },
        {
          path: "helpdesk/tickets/:id",
          name: "helpdesk-ticket-detail",
          component: () => import("@/views/helpdesk/TicketDetailView.vue"),
        },
        {
          path: "helpdesk/templates",
          name: "helpdesk-templates",
          component: () => import("@/views/helpdesk/TemplatesView.vue"),
        },
        {
          path: "helpdesk/console",
          name: "helpdesk-console",
          component: () => import("@/views/helpdesk/HelpdeskConsoleView.vue"),
          meta: { fullscreen: true },
        },
        {
          path: "saas/installations",
          name: "saas-installations",
          component: () => import("@/views/saas/InstallationsView.vue"),
        },
        {
          path: "saas/installations/:id/edit",
          name: "saas-installation-edit",
          component: () => import("@/views/saas/InstallationFormView.vue"),
        },
        {
          path: "saas/installations/:id/events",
          name: "saas-installation-events",
          component: () => import("@/views/saas/AuditLogView.vue"),
        },
        {
          path: "invoices/:id",
          name: "invoice-detail",
          component: () => import("@/views/customers/InvoiceDetailView.vue"),
        },
        {
          path: "tasks/my",
          name: "my-tasks",
          component: () => import("@/views/tasks/MyTasksView.vue"),
        },
        {
          path: "tasks",
          name: "tasks",
          component: () => import("@/views/tasks/TaskListView.vue"),
        },
        {
          path: "tasks/calendar",
          name: "tasks-calendar",
          component: () => import("@/views/tasks/TaskCalendarView.vue"),
        },
        {
          path: "tasks/agenda",
          name: "tasks-agenda",
          component: () => import("@/views/tasks/TaskAgendaView.vue"),
        },
        // CRM Tasks
        {
          path: "crm/tasks",
          name: "crm-tasks",
          component: () => import("@/views/tasks/TaskListView.vue"),
        },
        // Field Operations Tasks
        {
          path: "field/installations",
          name: "field-installations",
          component: () => import("@/views/tasks/TaskListView.vue"),
        },
        {
          path: "field/technicians",
          name: "field-technicians",
          component: () => import("@/views/tasks/TaskListView.vue"), // Or specific view if exists, defaulting to tasks
        },
        // ERP Maintenance Tasks
        {
          path: "erp/maintenance",
          name: "erp-maintenance",
          component: () => import("@/views/tasks/TaskListView.vue"),
        },
        // PLEX Field Operations
        {
          path: 'plex/kanban',
          name: 'plex-kanban',
          component: () => import('@/views/tasks/plex/PlexKanbanView.vue'),
          meta: { title: 'PLEX - Kanban', permission: 'tasks.field.read' },
        },
        {
          path: 'plex/tasks',
          name: 'plex-tasks',
          component: () => import('@/views/tasks/plex/PlexTaskListView.vue'),
          meta: { title: 'PLEX - Lista', permission: 'tasks.field.read' },
        },
        {
          path: 'plex/tasks/new',
          name: 'plex-task-new',
          component: () => import('@/views/tasks/plex/PlexTaskFormView.vue'),
          meta: { title: 'PLEX - Nueva Tarea', requiresAuth: true, permission: 'tasks.field.write' },
        },
        {
          path: "erp/subscriptions",
          name: "erp-subscriptions",
          component: () => import("@/views/sales/SubscriptionsView.vue"),
        },
        {
          path: "erp/subscriptions/:id",
          name: "erp-subscription-detail",
          component: () => import("@/views/sales/SubscriptionDetailView.vue"),
        },
        // Network Routes
        {
          path: "network/ipam",
          name: "network-ipam",
          component: () => import("@/views/network/IpamView.vue"),
        },
        // Monitoring Routes
        {
          path: "monitor/dashboard",
          name: "monitor-dashboard",
          component: () => import("@/views/monitor/DashboardView.vue"),
        },
        {
          path: "monitor/alerts",
          name: "monitor-alerts",
          component: () => import("@/views/monitor/AlertsView.vue"),
        },
        {
          path: "monitor/rules",
          name: "monitor-rules",
          component: () => import("@/views/monitor/RulesView.vue"),
        },
        {
          path: "network/equipment",
          name: "network-equipment",
          component: () =>
            import("@/views/network/NetworkDevicesView.vue"),
        },
        {
          path: "network/pppoe",
          name: "network-pppoe",
          component: () =>
            import("@/views/network/PppoeSessionsView.vue"),
        },
        {
          path: "network/cpes",
          name: "network-cpes",
          component: () => import("@/views/network/CpesView.vue"),
        },
        // SDD 10: Networking infrastructure views
        {
          path: "network/nodes",
          name: "network-nodes",
          component: () => import("@/views/network/NodesView.vue"),
        },
        {
          path: "network/nodes/:id",
          name: "network-node-detail",
          component: () => import("@/views/network/NodeDetailView.vue"),
        },
        {
          path: "network/auth-servers",
          name: "network-auth-servers",
          component: () => import("@/views/network/AuthServersView.vue"),
        },
        {
          path: "network/auth-servers/:id",
          name: "network-auth-server-detail",
          component: () =>
            import("@/views/network/AuthServerDetailView.vue"),
        },
        {
          path: "network/nat-servers",
          name: "network-nat-servers",
          component: () => import("@/views/network/NatServersView.vue"),
        },
        {
          path: "network/nat-servers/:id",
          name: "network-nat-server-detail",
          component: () =>
            import("@/views/network/NatServerDetailView.vue"),
        },
        {
          path: "network/devices",
          name: "network-devices",
          component: () => import("@/views/network/NetworkDevicesView.vue"),
        },
        {
          path: "network/devices/:id",
          name: "network-device-detail",
          component: () =>
            import("@/views/network/DeviceDetailView.vue"),
        },
        // SDD-93 T2.4: censo asistido de ONUs / carga de NAPs.
        {
          path: "network/fiber-census/:olt?",
          name: "network-fiber-census",
          component: () => import("@/views/network/FiberCensusView.vue"),
        },
        // Gestión y Topología de Cajas NAP (ODN)
        {
          path: "network/naps",
          name: "network-naps",
          component: () => import("@/views/network/NapBoxesView.vue"),
          meta: { title: "Cajas NAP (ODN) - ISPManager", requiresAuth: true },
        },
        // SDD-0140: Radar de Salud Óptica GPON (PON Atenuados vs NAP vs Drop)
        {
          path: "network/gpon-health",
          name: "network-gpon-health",
          component: () => import("@/views/network/GponPonHealthView.vue"),
          meta: { title: "Salud Óptica PON - ISPManager", requiresAuth: true },
        },
        {
          path: "tasks/settings/labels",
          name: "tasks-settings-labels",
          component: () => import("@/views/tasks/settings/TaskLabelsSettingsView.vue"),
        },
        {
          path: "tasks/new",
          name: "tasks-create",
          component: () => import("@/views/tasks/TaskFormView.vue"),
        },
        {
          path: "tasks/:id",
          name: "tasks-detail",
          component: () => import("@/views/tasks/TaskDetailView.vue"),
        },
        {
          path: "tasks/:id/edit",
          name: "tasks-edit",
          component: () => import("@/views/tasks/TaskFormView.vue"),
        },
        {
          path: "tasks/:id/execute",
          name: "tasks-execute",
          component: () => import("@/views/tasks/execute/TaskExecutionLayout.vue"),
        },
        // OTT / Sentinel Routes
        {
          path: "ott/settings/categories",
          name: "ott-categories",
          component: () => import("../views/ott/CategoriesView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Categorías OTT",
          },
        },
        {
          path: "ott/settings/categories/:id",
          name: "ott-category-detail",
          component: () => import("../views/ott/CategoryDetailView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Detalle de Categoría",
          },
        },
        {
          path: "ott/settings/classifications",
          name: "ott-classifications",
          component: () => import("../views/ott/ClassificationsView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Clasificaciones OTT",
          },
        },
        {
          path: "ott/settings/classifications/:id",
          name: "ott-classification-detail",
          component: () => import("../views/ott/ClassificationDetailView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Detalle de Clasificación",
          },
        },
        {
          path: "ott/settings/encoding-profiles",
          name: "ott-encoding-profiles",
          component: () => import("../views/ott/EncodingProfilesView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Perfiles de Codificación",
          },
        },
        {
          path: "ott/settings/encoding-profiles/:id",
          name: "ott-encoding-profile-detail",
          component: () => import("../views/ott/EncodingProfileFormView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Detalle de Perfil",
          },
        },
        {
          path: "ott/settings/languages",
          name: "ott-languages",
          component: () => import("../views/ott/LanguagesView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Idiomas OTT",
          },
        },
        {
          path: "ott/settings/languages/:id",
          name: "ott-language-detail",
          component: () => import("../views/ott/LanguageDetailView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Detalle de Idioma",
          },
        },
        {
          path: "ott/settings/agents",
          name: "ott-agents",
          component: () => import("../views/ott/AgentsView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Agentes OTT",
          },
        },
        {
          path: "ott/settings/agents/:id",
          name: "ott-agent-detail",
          component: () => import("../views/ott/AgentDetailView.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["ott.settings.read"],
            title: "Detalle de Agente",
          },
        },
        {
          path: "ott/stb-devices",
          name: "ott-stb-devices",
          component: () => import("@/views/ott/StbDevicesView.vue"),
        },
        {
          path: "ott/client-devices",
          name: "ott-client-devices",
          component: () => import("@/views/ott/ClientDevicesView.vue"),
        },
        {
          path: "ott/movies",
          name: "ott-movies",
          component: () => import("@/views/ott/MoviesView.vue"),
        },
        {
          path: "ott/movies/:id",
          name: "ott-movie-detail",
          component: () => import("@/views/ott/MovieDetailView.vue"),
        },
        {
          path: "ott/series",
          name: "ott-series",
          component: () => import("@/views/ott/SeriesView.vue"),
        },
        {
          path: "ott/series/:id",
          name: "ott-series-detail",
          component: () => import("@/views/ott/SeriesDetailView.vue"),
        },
        {
          path: "ott/seasons",
          name: "ott-seasons",
          component: () => import("@/views/ott/SeasonsView.vue"),
        },
        {
          path: "ott/seasons/:id",
          name: "ott-seasons-detail",
          component: () => import("@/views/ott/SeasonDetailView.vue"),
        },
        {
          path: "ott/series/:id/episodes",
          name: "ott-series-episodes",
          component: () => import("@/views/ott/EpisodesView.vue"),
        },
        {
          path: "ott/episodes/:id",
          name: "ott-episode-detail",
          component: () => import("@/views/ott/EpisodeDetailView.vue"),
        },
        {
          path: "netplay",
          component: () => import("@/components/layout/NetplayLayout.vue"),
          children: [
            {
              path: "",
              redirect: { name: "netplay-dashboard" },
            },
            {
              path: "dashboard",
              name: "netplay-dashboard",
              component: () => import("@/views/netplay/DashboardView.vue"),
              meta: { permissions: ["netplay.dashboard.read"] },
            },
            {
              path: 'cash-sessions-report',
              name: 'erp.accounting.cash-sessions-report',
              component: () => import('@/views/erp/accounting/CashSessionsReportView.vue'),
              meta: { breadcrumb: 'Reporte de Cajas', permission: 'reports.financial.read' }
            },
            {
              path: "customers",
              name: "netplay-customers",
              component: () => import("@/views/netplay/CustomersListView.vue"),
              meta: { permissions: ["netplay.customers.read"] },
            },
            {
              path: "customers/:id",
              name: "netplay-customer-detail",
              component: () => import("@/views/netplay/CustomerDetailView.vue"),
              meta: { permissions: ["netplay.customers.read"] },
            },
            {
              path: "user-agents",
              name: "netplay-user-agents",
              component: () =>
                import("@/views/netplay/AllowedUserAgentsView.vue"),
              meta: { permissions: ["netplay.user_agents.read"] },
            },
            {
              path: "inventory/stb",
              name: "netplay-inventory-stb",
              component: () =>
                import("@/views/netplay/StbInventoryView.vue"),
              meta: { permissions: ["netplay.devices.read"] },
            },
            {
              path: "inventory/mobile",
              name: "netplay-inventory-mobile",
              component: () =>
                import("@/views/netplay/MobileDevicesInventoryView.vue"),
              meta: { permissions: ["netplay.devices.read"] },
            },
          ],
        },
        {
          path: "ott/streams",
          name: "ott-streams",
          component: () => import("@/views/ott/StreamsView.vue"),
        },
        {
          path: "ott/streams/:id",
          name: "ott-stream-detail",
          component: () => import("@/views/ott/StreamDetailView.vue"),
        },
        {
          path: "ott/channels",
          name: "ott-channels",
          component: () => import("@/views/ott/ChannelsView.vue"),
        },
        {
          path: "ott/channels/:id",
          name: "ott-channel-detail",
          component: () => import("@/views/ott/ChannelDetailView.vue"),
        },
        {
          path: "admin/notification-templates",
          name: "admin-notification-templates",
          component: () => import("@/views/admin/NotificationTemplatesView.vue"),
          meta: { requiresAdmin: true },
        },
        {
          path: "admin/payment-gateways",
          alias: ["billing/payments/gateways-admin"],
          name: "admin-payment-gateways",
          component: () => import("@/views/admin/TenantPaymentGatewaysAdminView.vue"),
          meta: {
            requiresAuth: true,
            title: "Gobierno de Pasarelas de Pago por Tenant",
          },
        },
      ],
    },
    {
      path: "/unauthorized",
      name: "unauthorized",
      component: () => import("@/views/UnauthorizedView.vue"),
      meta: { requiresAuth: false, title: "Acceso Denegado" },
    },
    {
      path: "/forbidden",
      name: "forbidden",
      redirect: "/unauthorized",
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("@/views/NotFoundView.vue"),
    },
  ],
});

router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore();

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: "login", query: { redirect: to.fullPath } });
    return;
  }

  // Si navega a vistas de estado de autenticación, permitir el paso
  if (
    to.name === "unauthorized" ||
    to.name === "forbidden" ||
    to.name === "login" ||
    to.name === "not-found"
  ) {
    next();
    return;
  }

  // Redirección directa de tenants de solo pasarela de pagos (Alcaldía de San Miguel Centro)
  if (
    to.name === "home" &&
    (authStore.hasRole("ALCALDIA_SAN_MIGUEL_ADMIN") ||
      (authStore.user?.email || "")
        .toLowerCase()
        .endsWith("@sanmiguel.gob.sv") ||
      (authStore.user?.email || "")
        .toLowerCase()
        .endsWith("@alcaldiasanmiguelcentro.com"))
  ) {
    next({ path: "/billing/payments" });
    return;
  }

  // Bloquear rutas /erp/* para usuarios sin permisos ni roles de ERP (ej. técnicos de fibra / NOC)
  if (to.path.startsWith("/erp")) {
    const roles = authStore.user?.roles || [];
    const perms = authStore.user?.permissions || [];
    const isSuperOrErpRole = roles.some((r) =>
      [
        "PLATFORM_SUPERADMIN",
        "isp_super_admin",
        "isp_admin",
        "admin",
        "TENANT_OWNER",
        "SUPERADMIN",
        "Owner",
        "owner",
        "CEO",
        "CFO",
        "COO",
        "DIRECTOR",
        "GERENTE_GENERAL",
        "EJECUTIVO",
        "TEAM_LEADER",
        "team_leader",
        "Team Leader",
        "Team_Leader",
        "isp_team_leader",
        "LIDER",
        "LIDER_TECNICO",
        "SUPERVISOR",
        "ERP_MANAGER",
        "BILLING_MANAGER",
        "FINANCIAL_MANAGER",
        "Contador",
        "isp_erp_admin",
        "isp_erp_operator",
        "isp_erp_viewer",
      ].includes(r)
    );
    const hasErpPerm =
      authStore.hasPermission("*") ||
      authStore.isAgencyManager ||
      perms.some(
        (p) =>
          p.startsWith("erp.") &&
          p !== "erp.inventory.assigned_read" &&
          p !== "erp:inventory:assigned_read"
      );

    if (!isSuperOrErpRole && !hasErpPerm) {
      next({ name: "unauthorized", query: { required: "erp.*" } });
      return;
    }
  }

  // Control de administrador
  if (to.meta.requiresAdmin) {
    const isAdmin =
      authStore.hasRole("admin") ||
      authStore.hasRole("PLATFORM_SUPERADMIN") ||
      authStore.hasPermission("*");
    if (!isAdmin) {
      next({ name: "unauthorized", query: { required: "role:admin" } });
      return;
    }
  }

  // Control de permiso individual (to.meta.permission)
  if (to.meta.permission) {
    const required = to.meta.permission as string;
    if (!authStore.hasPermission(required)) {
      next({ name: "unauthorized", query: { required } });
      return;
    }
  }

  // Control de lista de permisos (to.meta.permissions)
  if (to.meta.permissions) {
    const requiredPermissions = Array.isArray(to.meta.permissions)
      ? to.meta.permissions
      : [to.meta.permissions];

    const hasAccess = requiredPermissions.every((p: string) =>
      authStore.hasPermission(p)
    );
    if (!hasAccess) {
      next({
        name: "unauthorized",
        query: { required: requiredPermissions.join(", ") },
      });
      return;
    }
  }

  next();
});

export default router;
